import catalog from "../maps/catalog.json";
import { mapHubs, type HubSpot } from "./hubs";
import type { MapChallenge } from "./ladder";
import type { EdgeSide, Level } from "./level";
import { buildLevel, parseMapFile, type MapFile } from "./mapfile";
import { mirrorMapFile } from "./mirror";
import { DEFAULT_GOALS, type MapGoals } from "./run";
import { HILL_SITE } from "./site";
import hillFile from "../hills/bomb-the-hill.json";
import { isTask, taskOf, type TaskGoal } from "./tasks";

// The maps you can pick. Each is a JSON map file in src/maps/ (see
// mapfile.ts for the format): a level (sim data) plus how it looks (render
// hints: sky, lighting, walls). Kept free of three.js so the sim and the
// pause menu can use it in tests; Game turns `look` into a scene.
//
// Map files load on demand, so adding maps doesn't grow the startup bundle.
// What the picker needs up front (name, blurb, thumbnail) lives in
// src/maps/catalog.json and src/maps/thumbs/. Add a map by adding its file,
// a catalog entry (in picker order) and its thumbnail (`pnpm vitest -u`
// writes it; maps.test.ts checks all three agree).

/**
 * What's around an outdoor park: `park` (the default) is grass and trees,
 * `plaza` is paving with buildings round it (a street or school spot), and
 * `roof` puts the park on a rooftop, other buildings' roofs around it and
 * the street far below. `street` (#115) puts it on a city street running
 * east to west, at street level: building fronts stand right behind the
 * north and south walls (drawn instead of those walls), the east and west
 * walls are low road barriers (`wallHeight`), and the street goes on past
 * them between more buildings.
 */
export type Outside = "park" | "plaza" | "roof" | "beach" | "street";

/**
 * A map's mood (#179): the light it's meant to feel like, which picks the
 * Realistic style's color grade (`render/mood.ts`). Other styles ignore it.
 */
export type Mood = "noon" | "golden" | "overcast" | "dusk" | "tungsten";

/**
 * How a map's pieces look (#115), when not the skatepark's painted ramps:
 * bare concrete and granite in these colors, rails in galvanized steel. A
 * piece's own `color` beats its entry here.
 */
export interface PieceLook {
  /** Boxes, ledges and stair sets. */
  block: string;
  /** Kickers (banks) and quarter pipes. */
  ramp: string;
  /** Freestanding rails and handrails, and their posts. */
  rail: string;
}

export interface MapLook {
  /** Indoors: a roof and tall walls instead of sky, clouds and trees. */
  indoor: boolean;
  /** Outdoor maps only: what's around the fence (default `park`). */
  outside?: Outside;
  /** Background and fog color. */
  sky: string;
  /** The sky overhead (#82); left out, a deeper shade of `sky`. */
  skyTop?: string;
  fogNear: number;
  fogFar: number;
  /** Hemisphere (ambient) light. */
  hemiSky: string;
  hemiGround: string;
  hemiIntensity: number;
  /** The directional light: sunshine, or daylight through skylights indoors. */
  sunIntensity: number;
  /** Tints the art style's sunlight (#82: warm late-afternoon light); left out, the style's own. */
  sunTint?: string;
  /** The mood its Realistic color grade sets (#179); left out, noon outdoors and tungsten indoors. */
  mood?: Mood;
  /** Tint over the concrete floor texture. */
  floor: string;
  /** Perimeter wall colors, one per side (south, east, north, west). */
  walls: [string, string, string, string];
  wallHeight: number;
  /** Roof height, when indoor. The camera stays under it. */
  roof: number | null;
  /** The pieces' materials and colors (#115); left out, the painted skatepark look. */
  pieces?: PieceLook;
  /** Street ends (#236): stretches of wall drawn as a pedestrian zone's barriers with a street beyond. None when left out. */
  streetEnds?: StreetEnd[];
  /** Shop rows (#240): stretches of wall drawn as a row of shopfronts. None when left out. */
  shopRows?: ShopRow[];
}

/**
 * A street end (#236): from `from` to `to` along a wall side (x on the north
 * and south sides, z on the east and west), the wall is drawn as the line a
 * city closes a street off with (bollards, jersey barriers, planters), and a
 * street at height `y` goes on past it between shopfronts, so a deck built
 * against the wall there reads as the end of that street. Drawn only: the
 * sim still has its wall there (`outOfBounds`), so it's the park's edge.
 */
export interface StreetEnd {
  side: EdgeSide;
  from: number;
  to: number;
  y: number;
}

/**
 * A shop row (#240): from `from` to `to` along a wall side (x on the north
 * and south sides, z on the east and west), the wall is drawn as a row of
 * shopfronts standing on the fence line, a street spot's (`render/shop-row.ts`):
 * `shops` names them in order along the side (each a `SHOPS` name, so its
 * sign, awning, width and any room you can see into come with it). Drawn
 * only: the sim keeps its wall there, so you wallride the shopfronts.
 */
export interface ShopRow {
  side: EdgeSide;
  from: number;
  to: number;
  shops: string[];
}

export interface MapDef {
  id: string;
  name: string;
  blurb: string;
  /** Builds a fresh level; switching maps never shares state with the last run. */
  level: () => Level;
  look: MapLook;
  /** The 2-minute run's goal list. */
  goals: MapGoals;
  /** The challenge ladder (#66), from the map file; see `mapLadder`. */
  challenges?: readonly MapChallenge[];
  /** The challenge hubs (#86), in tier order; empty for a map without them (a marker per challenge). */
  hubs: readonly HubSpot[];
}

/** A map as the picker shows it, before its file has loaded. */
export interface MapInfo {
  id: string;
  name: string;
  blurb: string;
  /** URL of the top-down thumbnail (src/maps/thumbs/<id>.svg). */
  thumb: string;
}

// The hill site (#219) has no parks: none of these are in its bundle.
const FILES: Record<string, () => Promise<{ default: unknown }>> = HILL_SITE
  ? {}
  : import.meta.glob<{ default: unknown }>(["../maps/*.json", "!../maps/catalog.json"]);
const THUMBS: Record<string, string> = HILL_SITE
  ? {}
  : import.meta.glob<string>("../maps/thumbs/*.svg", { eager: true, query: "?url", import: "default" });

/** The maps, in picker order. The first is the default. Empty on the hill site (#219). */
export const MAP_CATALOG: readonly MapInfo[] = HILL_SITE
  ? []
  : catalog.map((m) => ({
      ...m,
      thumb: THUMBS[`../maps/thumbs/${m.id}.svg`] ?? "",
    }));

/** The map the game opens on: the first park, or Bomb the Hill on the hill site (#219). */
export const DEFAULT_MAP_ID = MAP_CATALOG[0]?.id ?? "";

/**
 * Bomb the Hill's map file (#193), checked. Not a catalog map: a downhill
 * run isn't a park, so the park checks (flow, map rules) don't fit it. The
 * file is under 1 kB, so it's bundled rather than loaded on demand.
 */
export const HILL: MapFile = parseMapFile(hillFile);

/** The catalog entry for this id, or the default map's if there's no such map. */
export function mapInfo(id: string): MapInfo {
  return MAP_CATALOG.find((m) => m.id === id) ?? MAP_CATALOG[0]!;
}

/** Wraps a checked map file for the game: `level()` builds a fresh copy every time. */
export function mapFromFile(file: MapFile): MapDef {
  return {
    id: file.id,
    name: file.name,
    blurb: file.blurb,
    level: () => buildLevel(file),
    look: file.look,
    goals: mapGoals(file),
    challenges: file.challenges,
    hubs: mapHubs(file.hubs),
  };
}

/** A map file's goals, with its tasks (the `challenges` entries with a `kind`) added. */
export function mapGoals(file: MapFile): MapGoals {
  const goals = file.goals ?? DEFAULT_GOALS;
  const tasks = (file.challenges ?? []).filter(isTask).map((e) => taskOf(e as TaskGoal & Record<string, unknown>));
  return tasks.length ? { ...goals, tasks } : goals;
}

const loaded = new Map<string, Promise<MapFile>>();

/**
 * Loads and checks a map's file (the default map for an unknown id), flipped
 * left to right if `mirrored` (Mirror mode, #119). Each file is fetched once;
 * a failed load is forgotten so it can be retried.
 */
export function loadMapDef(id: string, mirrored = false): Promise<MapDef> {
  const info = mapInfo(id);
  let file = loaded.get(info.id);
  if (!file) {
    const load = FILES[`../maps/${info.id}.json`];
    file = load
      ? load().then((m) => parseMapFile(m.default))
      : Promise.reject(new Error(`No map file for "${info.id}"`));
    loaded.set(info.id, file);
    file.catch(() => loaded.delete(info.id));
  }
  return file.then((f) => mapFromFile(mirrored ? mirrorMapFile(f) : f));
}
